import {useUpmPlant} from '@hconnect/upm'
import {Button, H5, Loading, spawnErrorToast} from '@hm/fundamental'
import {mdiPlus} from '@mdi/js'
import {ChangeEvent, useRef} from 'react'
import {useTranslation} from 'react-i18next'

import {useAddInstallationDocuments, useDeleteInstallationDocument} from './hooks'

import {AttachedDocumentFileView} from '@refra/modules/common/components/documents'
import {useAttachedDocumentsFilesQuery} from '@refra/modules/common/hooks/queries'
import {useKilns} from '@refra/modules/common/providers'
import {checkFileFormat, checkFileSize} from '@refra/modules/common/utils'
import type {MaterialInstallationDetails} from '@refra/modules/refra-planning/types'
import {useUrlParam} from '@refra/modules/routing/hooks'

interface InstallationDocumentsProps {
  materialInstallationDetails: MaterialInstallationDetails
  shutdownId: number
  isReadOnly?: boolean
}

export const InstallationDocuments: React.FC<InstallationDocumentsProps> = ({
  materialInstallationDetails,
  shutdownId,
  isReadOnly = false
}) => {
  const {t} = useTranslation()

  const plantId = useUrlParam('plantCode')
  const upmPlant = useUpmPlant({plantId})
  const {selectedKiln} = useKilns()

  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const materialInstallationDocuments = materialInstallationDetails.documents ?? []

  const documentsSectionTitle = `${t('common.attachments')} (${
    materialInstallationDocuments.length
  })`

  const {data: attachedDocumentsFiles} = useAttachedDocumentsFilesQuery({
    documents: materialInstallationDocuments
  })

  const {mutate: addDocuments, isPending: isAddingDocuments} = useAddInstallationDocuments()
  const {mutate: deleteDocument, isPending: isDeletingDocument} = useDeleteInstallationDocument()

  const onAddDocumentClick = (e) => {
    e.preventDefault()
    fileInputRef?.current?.click()
  }

  const onDeleteDocumentClick = (documentId: number) => {
    return deleteDocument({
      plantUpmId: upmPlant.upmId,
      kilnUpmId: selectedKiln.upmId,
      shutdownId,
      installationId: materialInstallationDetails.id,
      documentId: documentId
    })
  }

  const onFileUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (!selectedFile) return

    if (!checkFileSize(selectedFile)) {
      return spawnErrorToast(t('error.fileTooLarge'))
    }

    if (!checkFileFormat(selectedFile)) {
      return spawnErrorToast(t('error.fileFormat'))
    }

    return addDocuments({
      plantUpmId: upmPlant.upmId,
      kilnUpmId: selectedKiln.upmId,
      shutdownId,
      installationId: materialInstallationDetails.id,
      documents: [selectedFile]
    })
  }

  if (!attachedDocumentsFiles) {
    return (
      <div className="flex justify-center">
        <Loading size="large" />
      </div>
    )
  }

  const areDocumentsDisabled = isReadOnly || isAddingDocuments || isDeletingDocument

  return (
    <div className="flex flex-col gap-5">
      <div className="flex justify-between items-center">
        <H5 data-cy="material_modal_documents_title">{documentsSectionTitle}</H5>
        <Button
          icon={mdiPlus}
          variant="tertiary"
          round
          square
          size="small"
          disabled={areDocumentsDisabled}
          onClick={onAddDocumentClick}
          data-cy="material_modal_add_document_button"
        />
        <input
          type="file"
          ref={fileInputRef}
          disabled={areDocumentsDisabled}
          onChange={onFileUpload}
          className="hidden"
          data-cy="material_modal_documents_input"
        />
      </div>
      <div className="flex flex-wrap gap-3">
        {attachedDocumentsFiles.map((attachedDocumentFile) => (
          <div key={attachedDocumentFile.id}>
            <AttachedDocumentFileView
              attachedDocumentFile={attachedDocumentFile}
              isDisabled={areDocumentsDisabled}
              iconSize={2}
              className="max-w-[220px] max-h-[110px]"
              onDocumentDelete={() => onDeleteDocumentClick(attachedDocumentFile.id)}
            />
          </div>
        ))}
      </div>
    </div>
  )
}
